import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import {
  EnrollmentPeriod,
  EnrollmentStage,
  EnrollmentType,
  InputProposalV2,
  InstalledProductLine,
  ProductLine,
  ProductType,
  Proposal,
  ProposalAction,
  InitiateType,
  SYSTEM_GENERATED,
  USER_ACTIONS,
} from 'types/enrollmentService';
import { Cart } from 'types/cartServiceV2';
import { AssigneeType, Group } from 'types/groupService';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { INITIAL_ENROLLMENT_OBJECT } from 'store/modules/enrollment/enrollmentPeriodModule';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { CopyHelper } from 'global/utils/CopyHelper';
import { isEffectiveDatePassed } from 'global/utils/dateHelper';
import {
  CartItem,
  MarketingTag,
  Portfolio,
  ProductBcInfo,
  ProductLineAttr,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { Catalog } from 'types/productServiceV2';
import { INITIAL_CART_OBJECT } from 'store/domain/cartStoreV2';
import { isEnrollmentPeriodNotEditable } from 'global/checklists-helpers/ChecklistHelper';
import { BrowsingType, Config, DomainProductLine, InputFilter } from 'types/domainServiceV2';
import { INITIAL_DOMAIN_CONFIGURATION } from 'store/domain/domainStoreV2';
import {
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { eeRights, employerRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { FetchPolicy } from '@apollo/client';
import { PortfolioItemsByProductLine } from 'store/modules/proposal/types';
import { confirmCartItems as confirmCartItemsInCartService, copyCart, getCart } from 'api/operations/cartServiceV2';
import { getCatalog, getPortfoliosById, removeCollateral, removeCustomRates } from 'api/operations/productServiceV2';
import { getConfigByDate, getDomainProductLine } from 'api/operations/domainServiceV2';
import { domainServiceHelper } from 'global/services-helpers';
import { requestMarkFormsAsInactive } from 'api/operations/formService';
import {
  applyProposalUserAction,
  confirmCartItems as confirmCartItemsInEnrollmentService,
  getAllEnrollmentPeriodsByGroupId,
  rollbackEnrollment,
  saveProposalChoices,
  initiateProposal,
} from 'api/operations/enrollmentService';
import { renewalProductLineName } from 'global/services-helpers/enrollmentService';
import { getRFQProposalChecklistURL } from 'global/checklists-helpers/RFQProposalChecklistHelper';
import { INITIAL_CATALOG } from 'global/services-helpers/productService';
import { GuidelineDTO, UwGuideline } from 'types/uwService';
import { getGuidelines } from 'api/operations/uwService';
import { getAttributeValues } from 'global/utils/portfolioHelper';
import { getRequiredCrossSellViolations } from 'global/utils/crossSellHelper';
import { updateGroup } from 'api/operations/groupService';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { generateProposal } from 'api/operations/proposalServiceV3';
import { ProposalValidationError } from 'types/proposalService';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';

export enum ProposalConfirmationStatus {
  NEW,
  CONFIRMED,
  SWITCH,
  SWAP,
  SUBMITTED,
  ARCHIVED,
  SOLD,
}

interface PushFn {
  (link: string): void;
}

interface RemovedRateInfo {
  cartId: string;
  portfolioId: string;
  shortName: string;
  bcIndex: number;
}

interface RemovedFileInfo {
  portfolioId: string;
  productId: string;
  linkName: string;
}

export class ProposalItemsStore {
  rootStore: RootStore;
  loading = false;

  proposal: Proposal = {
    // entity that includes basic data about proposal
    cartId: '',
    name: '',
    createdBy: {
      id: '',
      name: '',
    },
    productLines: [],
    confirmed: false,
    rateSegment: 'GROUP',
  };

  /**
   * Property used to store state that "Confirm Proposal" button enabled or disabled to click
   */
  confirmProposalEnabled = false;
  /**
   * Cart used to store id, or browsing criteria, but don't grab CartItems from it
   */
  cart: Omit<Cart, 'cartItems'>;
  /**
   * Used to get info about group and agent that assigned for it
   */
  group: Group;
  /**
   * Represents enrollment period to which the  Proposal belongs
   */
  enrollmentPeriod: EnrollmentPeriod;
  /**
   *  Represents enrollment period with confirm proposal that in Enrolling stage for current group
   *   and used to restrict ability to have 2 "Active" periods simultaneously
   */
  otherEnrollmentIsActiveForThisGroup: EnrollmentPeriod | undefined;
  /**
   * List of all possible tags that can be shown up on Proposal Card
   */
  marketingTags: MarketingTag[] = [];
  /**
   * Used to identify domain specific data by effective date
   */
  domainConfig: Config;
  /**
   * Required to build Proposal Pdf and generate Annual Premium File
   */
  catalog: Catalog;
  portfolios: Portfolio[] = [];
  /**
   * Main object in the store, on it all Cards rendered
   */
  portfoliosByProductLine: PortfolioItemsByProductLine[] = [];
  /**
   * Used to store which Proposal card should have a spinner, because some info recalculating
   */
  portfolioLoading: Record<string, boolean> = {};
  /**
   * List of all product lines for displaying configure a plan buttons. Note(!) Only Product lines with the same rate segment
   */
  productLines: DomainProductLine[] = [];

  areAllPortfoliosWithRates = false;
  showRemoveRateModal = false;
  showRemoveFileModal = false;
  removedRateInfo: RemovedRateInfo = {
    cartId: '',
    portfolioId: '',
    shortName: '',
    bcIndex: 0,
  };

  removedFileInfo: RemovedFileInfo = {
    portfolioId: '',
    productId: '',
    linkName: '',
  };

  uwGuidelines: UwGuideline[] = [];

  public async collectProposalData(groupId: string, cartId: string, periodId: string): Promise<void> {
    this.setLoading(true);
    const requests: (Promise<Cart> | Promise<EnrollmentPeriod[]> | Promise<void>)[] = [
      getCart(cartId),
      this.rootStore.groupStore.getGroupById(groupId),
      this.rootStore.enrollmentStore.getEnrollmentPeriod(periodId),
      getAllEnrollmentPeriodsByGroupId(groupId),
    ];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const results = await Promise.all(requests);

    await runInAction(async () => {
      // Copy basic types to avoid possible override
      const cartData = CopyHelper.deepCopy(results[0] as Cart);
      const groupData = CopyHelper.deepCopy(this.rootStore.groupStore.group);
      this.cart = cartData;
      this.group = groupData;
      this.enrollmentPeriod = CopyHelper.deepCopy(this.rootStore.enrollmentStore.enrollmentPeriod);
      this.otherEnrollmentIsActiveForThisGroup = (results[3] as EnrollmentPeriod[])
        .filter((period) => period.id !== this.enrollmentPeriod.id)
        .find((period) => period.stage === EnrollmentStage.ENROLLING); // "Active" enrollment period have "Enrolling" stage
      this.proposal = this.rootStore.enrollmentStore.getProposalById(cartId);

      const sponsorId = this.rootStore.userStore.sponsorId;

      const requestsForPortfolios = [
        getCatalog(
          sponsorId,
          this.cart.lastUpdateBrowsingCriteria.effectiveDate,
          this.proposal.rateSegment,
          this.cart.lastUpdateBrowsingCriteria.state,
        ),
        getPortfoliosById(
          cartData.cartItems.map((item) => toJS(item)).slice(),
          undefined,
          cartData.groupId,
          cartData.id,
          'no-cache',
        ),
        getConfigByDate(sponsorId, this.cart.lastUpdateBrowsingCriteria.effectiveDate),
        this.getProductLines(cartData),
      ];

      const promisesResult = await Promise.all(requestsForPortfolios);

      await runInAction(async () => {
        const catalog = (promisesResult[0] as ApolloQueryResult<{ getCatalog: Catalog }>).data.getCatalog;
        const portfolios = promisesResult[1] as Portfolio[];
        this.portfolios = portfolios;
        this.checkPortfoliosRates(portfolios);
        const sortedPortfolios = portfolios
          .map((portfolio) => toJS(portfolio))
          .slice()
          .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
        this.portfoliosByProductLine = this.populatePortfolioByProductLine(
          sortedPortfolios,
          catalog.productLineAttributes,
          cartData.cartItems,
        ).sort((a, b) => {
          return sortByProductLineId(a.productLineId, b.productLineId);
        });
        this.catalog = catalog;
        this.marketingTags = catalog.marketingTags;
        this.domainConfig = promisesResult[2] as Config;
        this.confirmProposalEnabled = this.canConfirmProposal();
        await this.getGuidelines(
          this.portfoliosByProductLine.map((item) => ({
            productLineId: item.productLineId,
            effectiveDate: this.cart.lastUpdateBrowsingCriteria.effectiveDate,
            bc: this.cart.lastUpdateBrowsingCriteria,
          })),
        );
        this.setLoading(false);
      });
    });
  }

  public updateCart(cart: Cart): void {
    this.cart = cart;
  }

  public async editProposal(): Promise<void> {
    this.setLoading(true);
    const result = await this.rootStore.cartStoreV2.copyCart(this.cart.id);
    if (!result.data) {
      throw new Error(`Copy cart wasn't successful`);
    }
    const newCartId = result.data.copyCart.id;
    const selectedProductLineId = this.proposal.productLines[0].id;
    window.location.href = `/browsing?groupId=${this.group.id}&productLineId=${selectedProductLineId}&cartId=${newCartId}`;
  }

  public async rollbackEnrollment(): Promise<void> {
    this.setLoading(true);

    if (!this.otherEnrollmentIsActiveForThisGroup) {
      throw new Error(`Cannot rollback not existing enrollment`);
    }

    await rollbackEnrollment(this.otherEnrollmentIsActiveForThisGroup.id);
    runInAction(() => {
      this.setLoading(false);
    });
  }

  public async confirmProposal(proposalAction: ProposalAction | undefined, withConfirmation: boolean): Promise<void> {
    this.setLoading(true);
    const portfolioIdToSelectIndex: Record<string, number> = {};
    this.portfoliosByProductLine
      .map((item) => item.portfolioToCartItems)
      .forEach((list) => {
        list
          .filter((pair) => {
            return pair.cartItem.confirmed;
          })
          .forEach((pair) => {
            portfolioIdToSelectIndex[pair.cartItem.portfolioId] = pair.cartItem.selectedIndex;
          });
      });

    const inputProposal: InputProposalV2 = {
      cartId: this.cart.id,
      productLines: this.proposal.productLines.map((line) => ({
        ...line,
        bcIndex: line.confirmedPortfolioId ? portfolioIdToSelectIndex[line.confirmedPortfolioId] : 0,
      })),
      cartName: this.cart.cartName,
    };

    await requestMarkFormsAsInactive(this.enrollmentPeriod.id);
    await runInAction(async () => {
      const confirmedCart = await confirmCartItemsInCartService(this.cart.id, portfolioIdToSelectIndex);

      await runInAction(async () => {
        if (withConfirmation && proposalAction) {
          await Promise.all([
            confirmCartItemsInEnrollmentService(this.enrollmentPeriod.id, inputProposal, proposalAction),
            generateProposal(
              {
                domainId: this.rootStore.userStore.sponsorId,
                catalogId: this.catalog.id,
                cart: confirmedCart,
                portfolios: this.portfolios,
                period: this.enrollmentPeriod,
                // Old groups may have null agentOfRecord (AOR was not required when they were created).
                group: {
                  ...this.group,
                  agentOfRecord: this.group.agentOfRecord ?? { id: '', name: '', type: AssigneeType.AGENT },
                },
              },
              true,
            ),
          ]);
        } else {
          await saveProposalChoices(this.enrollmentPeriod.id, inputProposal);
        }

        runInAction(() => {
          this.setLoading(false);
        });
      });
    });
  }

  public async updateCartItem(
    cart: Cart,
    portfolioId: string,
    fetchPolicy: FetchPolicy = 'cache-first',
  ): Promise<void> {
    const cartItems = cart.cartItems.filter((item) => item.portfolioId === portfolioId); // contains only one element
    this.updateCartAfterUpdatingPortfolioBC(cart);

    const updatePortfolios = await getPortfoliosById(cartItems, undefined, cart.groupId, cart.id, fetchPolicy);
    runInAction(() => {
      updatePortfolios.forEach((portfolio) => {
        this.portfoliosByProductLine.forEach((item) => {
          const portfolioSearch = item.portfolioToCartItems.find(
            (portfolioToCartItem) => portfolioToCartItem.portfolio.id === portfolioId,
          );
          if (portfolioSearch) {
            const previouslySelectedIndex = portfolioSearch.cartItem.selectedIndex;
            portfolioSearch.portfolio = portfolio;
            portfolioSearch.cartItem = cartItems[0];
            this.setBrowsingCriteria(portfolioSearch.cartItem.portfolioId, previouslySelectedIndex);
          }
        });
        this.setLoadingForPortfolio(portfolio.id, false);
      });
    });
  }

  /**
   * Update all portfolios with updated rates.
   * @param cart - The cart containing updated cart items.
   * @param portfolioId - The ID of the portfolio currently being updated.
   * @param checkRates - A flag that indicates whether to check if all portfolios have rates.
   */
  public async updateCustomPortfolioAndCart(cart: Cart, portfolioId: string, checkRates = true): Promise<void> {
    const cartItems = cart.cartItems;
    this.updateCartAfterUpdatingPortfolioBC(cart);

    const updatedPortfolios = await getPortfoliosById(
      cartItems.map((item) => toJS(item)).slice(),
      undefined,
      cart.groupId,
      cart.id,
    );
    runInAction(() => {
      const portfolios = updatedPortfolios;
      const sortedPortfolios = portfolios
        .map((portfolio) => toJS(portfolio))
        .slice()
        .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

      this.portfolios = sortedPortfolios;

      this.portfoliosByProductLine = this.populatePortfolioByProductLine(
        sortedPortfolios,
        this.catalog.productLineAttributes,
        cartItems,
      ).sort((a, b) => {
        return sortByProductLineId(a.productLineId, b.productLineId);
      });
      this.setLoadingForPortfolio(portfolioId, false);
      if (checkRates) this.checkPortfoliosRates(sortedPortfolios);
    });
  }

  private checkPortfoliosRates(portfolios: Portfolio[]): void {
    this.areAllPortfoliosWithRates =
      portfolios.filter((portfolio) =>
        Object.values(portfolio.products[0].bcDependentData).some(
          (bcVariant) => !(bcVariant as ProductBcInfo).rates || (bcVariant as ProductBcInfo).rates.length === 0,
        ),
      ).length === 0;
  }

  public openRemoveRateModal(cartItem: CartItem, bcIndex: number): void {
    const shortName = cartItem?.browsingCriteria[bcIndex]?.shortName[cartItem.productLineId] || '';
    this.showRemoveRateModal = true;
    this.removedRateInfo = { cartId: this.cart.id, portfolioId: cartItem.portfolioId, bcIndex, shortName };
  }

  public resetRemovedRateInfo(): void {
    this.removedRateInfo = {
      cartId: '',
      portfolioId: '',
      shortName: '',
      bcIndex: 0,
    };
  }

  public closeRemoveRateModal(): void {
    this.showRemoveRateModal = false;
  }

  public openRemoveFileModal(portfolioId: string, productId: string, linkName: string): void {
    this.showRemoveFileModal = true;
    this.removedFileInfo = { portfolioId, productId, linkName };
  }

  public closeRemoveFileModal(): void {
    this.showRemoveFileModal = false;
  }

  public async removeProductRate({ cartId, portfolioId, bcIndex }: RemovedRateInfo): Promise<Cart | null> {
    await removeCustomRates(cartId, portfolioId, bcIndex);
    const res = await this.rootStore.cartStoreV2.removeBcByIndex(cartId, portfolioId, bcIndex);
    return res.data?.removeBCByIndex || null;
  }

  // eslint-disable-next-line class-methods-use-this
  public async removeCustomLink(
    cartId: string,
    portfolioId: string,
    productId: string,
    linkName: string,
  ): Promise<void> {
    await removeCollateral(cartId, portfolioId, productId, linkName);
  }

  public async completeStep(periodId: string, cartId: string, action: USER_ACTIONS): Promise<void> {
    this.setLoading(true);
    await applyProposalUserAction(periodId, cartId, action);
    runInAction(() => {
      this.setLoading(false);
    });
  }

  public getProductLineBrowsingType(productLineId: ProductLineId): BrowsingType {
    const currentProductLine = domainServiceHelper.getDomainProductLineById(productLineId, this.productLines);
    return currentProductLine?.browsingType || 'SHELF';
  }

  public getOverrideRatesEligible(productLineId: ProductLineId): boolean {
    const currentProductLine = domainServiceHelper.getDomainProductLineById(productLineId, this.productLines);
    return currentProductLine?.overrideRatesEligible || false;
  }

  public getProductLineRateFormId(productLineId: ProductLineId, carrierId: string): string {
    const currentProductLine = domainServiceHelper.getDomainProductLineById(productLineId, this.productLines);
    return currentProductLine
      ? domainServiceHelper.getRFQFormByKey(currentProductLine, carrierId, 'rateRfqFormId')
      : '';
  }

  public isDontProvideIneligible(productLineId: ProductLineId): boolean {
    const currentProductLine = domainServiceHelper.getDomainProductLineById(productLineId, this.productLines);
    return currentProductLine?.dontProvideIneligible || false;
  }

  // setters
  public dontProvideOrCancelCoverage(productLineId: ProductLineId): void {
    if (this.enrollmentPeriod.type === EnrollmentType.RENEWAL && this.isRenewalProductLine(productLineId)) {
      // TODO ADD modal here for cancel
      this.setCancelCoverage(productLineId);
    } else {
      this.setDontProvideProductLine(productLineId);
    }

    // Unconfirm CartItem if necessary
    const action = (cartItem: CartItem): void => {
      // eslint-disable-next-line no-return-assign,no-param-reassign
      cartItem.confirmed = false;
    };
    this.loopOverPortfoliosByLine(action, productLineId);

    // Recompute after unconfirming cart items so the cross-sell validation sees the updated `confirmed` flags
    this.confirmProposalEnabled = this.canConfirmProposal();
  }

  private setDontProvideProductLine(productLineId: ProductLineId): void {
    const productLine = this.getProductLineFromProposal(productLineId);
    // if plan was selected previously, on "Don't Provide" Action it should be unchecked
    if (productLine.confirmedPortfolioId != null) {
      productLine.confirmedPortfolioId = null;
    }
    const pLineIndex = this.proposal.productLines.indexOf(productLine);
    if (productLine.productType === ProductType.DONT_PROVIDE) {
      this.proposal.productLines[pLineIndex].productType = ProductType.NEW_SALE;
    } else {
      this.proposal.productLines[pLineIndex].productType = ProductType.DONT_PROVIDE;
    }
  }

  private setCancelCoverage(productLineId: ProductLineId): void {
    const productLine = this.getProductLineFromProposal(productLineId);

    const pLineIndex = this.proposal.productLines.indexOf(productLine);
    if (productLine.productType === ProductType.CANCEL) {
      productLine.confirmedPortfolioId = null;
      this.proposal.productLines[pLineIndex].productType = ProductType.ADD_BENEFIT;
    } else {
      const renewalConfirmedPortfolio = this.getRenewalConfirmedPortfolioId(productLine.id);
      productLine.confirmedPortfolioId = renewalConfirmedPortfolio;
      this.proposal.productLines[pLineIndex].productType = ProductType.CANCEL;
    }
  }

  public setCartItemValue(productLineId: ProductLineId, cartItemId: string, select: boolean): void {
    // cartItemValue = '1234' - id of selected portfolio
    // cartItemValue = null - if don't provide or not yet selected
    const productLineFromProposal = this.getProductLineFromProposal(productLineId);
    this.setConfirmedPortfolio(productLineFromProposal, cartItemId as ProductType, select);
    this.setProductLineType(productLineFromProposal, cartItemId as ProductType);

    // set selected CartItem as confirmed
    // For Renewal plans we don't create Cart, so CartItem doesn't exist for it
    const action = (cartItem: CartItem): void => {
      // Only one cartItem for product line should be selected, so if one plan selected, previous selected, should be unselected
      // eslint-disable-next-line no-return-assign,no-param-reassign
      cartItem.confirmed = cartItemId === cartItem.portfolioId ? select : false;

      // set browsing criteria of the selected item to the Proposal
      if (cartItem.portfolioId === cartItemId) {
        productLineFromProposal.browsingCriteria = cartItem.browsingCriteria[cartItem.selectedIndex];
      }
    };
    this.loopOverPortfoliosByLine(action, productLineId);

    this.confirmProposalEnabled = this.canConfirmProposal();
  }

  /**
   * if it's a Renewal Product line we should set SponsorId_RENEWAL_ProductLineId as confirmedPortfolioId
   * no matter if it's a RENEW, or CANCEL
   * default case for Renewal ProductLine - it's a case when we Change Plan, so we will have new Portfolio id
   * from our catalog
   * else case it's for NEW_SALE -> always
   *
   * @param productLine
   * @param cartItemId - it's portfolio id that we selected
   * @param select - responsible for marking if we re making action, or undo
   */
  public setConfirmedPortfolio(productLine: ProductLine, cartItemId: ProductType, select: boolean): void {
    if (this.isRenewalProductLine(productLine.id)) {
      switch (cartItemId) {
        case ProductType.RENEW:
        case ProductType.CANCEL: {
          // eslint-disable-next-line no-param-reassign
          productLine.confirmedPortfolioId = select ? this.getRenewalConfirmedPortfolioId(productLine.id) : null;
          break;
        }
        default: {
          // eslint-disable-next-line no-param-reassign
          productLine.confirmedPortfolioId = select ? cartItemId : null;
          break;
        }
      }
    } else {
      // eslint-disable-next-line no-param-reassign
      productLine.confirmedPortfolioId = select ? cartItemId : null;
    }
  }

  /**
   *
   * Method that based on selected value set ProductType
   * if it's a renewal product line:
   * RENEW and CANCEL - self-descriptive
   * CHANGE_PLAN - it's a case when we select another portfolio from our catalog
   * If Product Line is not a Renewal, but it's a Renewal Enrollment Period
   * that means we add new Product Line to the Product Line
   * else case - NEW_SALE
   * @param productLine
   * @param choice
   */
  public setProductLineType(productLine: ProductLine, choice: ProductType): void {
    if (this.isRenewalProductLine(productLine.id)) {
      // if it's a Renewal Product line we should set special Product Types
      switch (choice) {
        case ProductType.RENEW: {
          // eslint-disable-next-line no-param-reassign
          productLine.productType = ProductType.RENEW;
          break;
        }
        case ProductType.CANCEL: {
          // eslint-disable-next-line no-param-reassign
          productLine.productType = ProductType.CANCEL;
          break;
        }
        default: {
          // eslint-disable-next-line no-param-reassign
          productLine.productType = ProductType.CHANGE_PLAN;
          break;
        }
      }
    } else if (this.isRenewalEnrollment) {
      // eslint-disable-next-line no-param-reassign
      productLine.productType = ProductType.ADD_BENEFIT;
    } else {
      // eslint-disable-next-line no-param-reassign
      productLine.productType = ProductType.NEW_SALE;
    }
  }

  public setBrowsingCriteria(id: string, selectedBcIndex: number): void {
    const action = (cartItem: CartItem): void => {
      // only set for the selected CartItem
      if (id === cartItem.portfolioId) {
        // update index in CartItem object
        // eslint-disable-next-line no-return-assign,no-param-reassign
        cartItem.selectedIndex = selectedBcIndex;

        // set selected browsing criteria in the Proposal
        const productLineFromProposal = this.getProductLineFromProposal(cartItem.productLineId);
        productLineFromProposal.browsingCriteria = cartItem.browsingCriteria[cartItem.selectedIndex];
      }
    };
    this.loopOverPortfoliosByLine(action);
  }

  public setLoadingForPortfolio(portfolioId: string, status: boolean): void {
    this.portfolioLoading[portfolioId] = status;
  }

  /**
   * This function triggers a rerender of the proposal card in case of a network error
   * and sets the status to false for the portfolio that threw an error.
   */
  public setPortfolioLoadingMapping(portfolioMapping: Record<string, boolean>): void {
    this.portfolioLoading = portfolioMapping;
  }

  // getters
  public get proposalConfirmationStatus(): ProposalConfirmationStatus {
    if (!this.enrollmentPeriodNotYetSubmitted()) {
      return ProposalConfirmationStatus.SUBMITTED;
    }
    if (this.otherEnrollmentIsActiveForThisGroup?.id) {
      return ProposalConfirmationStatus.SWAP;
    }
    if (this.enrollmentPeriod.stage === EnrollmentStage.ARCHIVED) return ProposalConfirmationStatus.ARCHIVED;
    if (this.enrollmentPeriod.stage === EnrollmentStage.SOLD) return ProposalConfirmationStatus.SOLD;

    // eslint-disable-next-line no-nested-ternary
    return this.enrollmentPeriod.hasConfirmedProposal
      ? this.proposal.confirmed
        ? ProposalConfirmationStatus.CONFIRMED
        : ProposalConfirmationStatus.SWITCH
      : ProposalConfirmationStatus.NEW;
  }

  public get fullCart(): Cart {
    return {
      ...this.cart,
      cartItems: this.cartItems,
    };
  }

  get goBackUrl(): string {
    if (this.proposal.confirmed) {
      return `/checklist/${this.enrollmentPeriod.id}`;
    }
    return `/group/${this.enrollmentPeriod.groupId}`;
  }

  // redirects
  compareAllPortfoliosInPl(productLineId: ProductLineId): void {
    window.location.href = `/product-compare?type=proposal&cartId=${this.cart.id}&productLineId=${productLineId}`;
  }

  onViewPlanDetails(portfolioId: string, pushFn: PushFn): void {
    const productLineId = this.getPortfolioById(portfolioId)?.productLineId;
    if (!productLineId) return;

    pushFn(
      `/product-compare?type=proposal-details&portfolioId=${portfolioId}&cartId=${this.cart.id}&productLineId=${productLineId}`,
    );
  }

  public get isProposalEditable(): boolean {
    const convertedRoles = this.rootStore.userStore.userRoles.map((role) => toJS(role)).slice();
    const isEmployer = employerRights(convertedRoles);
    const isPeriodNotEditable =
      isEnrollmentPeriodNotEditable(this.enrollmentPeriod.stage) ||
      this.domainConfig[this.enrollmentPeriod.rateSegment]?.proposalConfigIneligible ||
      false;

    return isEmployer
      ? !isPeriodNotEditable && this.proposalConfirmationStatus === ProposalConfirmationStatus.NEW
      : !isPeriodNotEditable;
  }

  public get isProposalConfirmed(): boolean {
    return this.proposal.confirmed;
  }

  public isPlanConfigurable(portfolio: Portfolio): boolean {
    if (this.isPortfolioDynamicallyCreated(portfolio)) return false;

    const isProductLineConfigurable = !!this.productLines
      .map((productLine) => toJS(productLine))
      .slice()
      .find(({ productLineId }) => portfolio.productLineId === productLineId)?.configurablePlans;

    return !this.isProposalConfirmed && isProductLineConfigurable;
  }

  // eslint-disable-next-line class-methods-use-this
  public isPortfolioDynamicallyCreated(portfolio: Portfolio): boolean {
    return portfolio.catalogId === '-1';
  }

  async getProductLines(cart: Cart): Promise<void> {
    const inputFilter: InputFilter = {
      effectiveDate: cart.lastUpdateBrowsingCriteria.effectiveDate,
      zipCode: cart.lastUpdateBrowsingCriteria.zipCode || '',
      state: cart.lastUpdateBrowsingCriteria.state || '',
      county: cart.lastUpdateBrowsingCriteria.county || '',
      sic: cart.lastUpdateBrowsingCriteria.sic4 || '',
      agencyId: this.rootStore.userStore.imAgencyId,
      sponsorId: this.rootStore.userStore.sponsorId,
    };

    this.productLines = await getDomainProductLine(inputFilter);
  }

  public get cartItems(): CartItem[] {
    return this.portfoliosByProductLine
      .map((line) => line.portfolioToCartItems)
      .map((item) => item.map((pair) => pair.cartItem))
      .flat();
  }

  public getProductLineFromProposal(productLineId: ProductLineId): ProductLine {
    const productLine = this.proposal.productLines.find((pLine) => pLine.id === productLineId);
    if (productLine) {
      return productLine;
    }
    throw new Error(`No productLine in Proposal with id: ${productLineId}`);
  }

  public getConfirmedPortfolioByProductLine(productLineId: ProductLineId): Portfolio | undefined {
    const confirmedPortfolioId = this.getProductLineFromProposal(productLineId).confirmedPortfolioId;
    return confirmedPortfolioId ? this.getPortfolioById(confirmedPortfolioId) : undefined;
  }

  // This method for Renewal case should check also if the Product Line Canceled
  public isProductLineDontProvide(productLineId: ProductLineId): boolean {
    const productType = this.getProductLineFromProposal(productLineId).productType;
    return productType === ProductType.DONT_PROVIDE || productType === ProductType.CANCEL;
  }

  public getPortfolioById(portfolioId: string): Portfolio | undefined {
    const portfolioByIdSearch = this.portfoliosByProductLine
      .map((item) => {
        const portfolioSearch = item.portfolioToCartItems.find(
          (portfolioToCartItem) => portfolioToCartItem.portfolio.id === portfolioId,
        );
        return portfolioSearch?.portfolio;
      })
      .filter((portfolio) => !!portfolio);

    if (portfolioByIdSearch.length === 1) {
      return portfolioByIdSearch[0];
    }
    return undefined;
  }

  public renewAsIsSelected(productLineId: ProductLineId): boolean {
    const productLine = this.getProductLineFromProposal(productLineId);
    const renewalPortfolioId = this.getRenewalConfirmedPortfolioId(productLineId);
    return productLine.confirmedPortfolioId === renewalPortfolioId && productLine.productType === ProductType.RENEW;
  }

  // private
  public getRenewalConfirmedPortfolioId(productLineId: ProductLineId): string {
    return renewalProductLineName(this.rootStore.userStore.sponsorId, productLineId);
  }

  public isAllProductLinesSelected(): boolean {
    return this.proposal.productLines
      .filter((pLine) => PRODUCT_LINES_CONTENT_MAP[pLine.id]?.rateSegment === this.proposal.rateSegment)
      .every(
        (pLine) =>
          pLine.confirmedPortfolioId !== null ||
          pLine.productType === ProductType.DONT_PROVIDE ||
          pLine.productType === ProductType.CANCEL,
      );
  }

  async getGuidelines(guidelineDTOs: GuidelineDTO[]): Promise<void> {
    this.uwGuidelines = await getGuidelines(guidelineDTOs);
  }

  public get productLineValidityLists(): {
    invalidPLines: ProductLineId[];
    notSelectedDependentPLines: ProductLineId[];
  } {
    const invalidProductLines: ProductLineId[] = [];
    const notSelectedDependentPLines: ProductLineId[] = [];
    this.proposal.productLines.forEach((pLine) => {
      const currentDomainPLine = this.productLines.find((domainPLine) => domainPLine.productLineId === pLine.id);
      if (!currentDomainPLine?.dependentOnProductLineId) return; // Skip product lines without dependencies

      const dependentProposalPLine = this.proposal.productLines.find(
        (proposalPLine) => proposalPLine.id === currentDomainPLine?.dependentOnProductLineId,
      );

      const isCurrentPLineSelected = pLine.confirmedPortfolioId !== null;
      const isDependentPLineSelected = dependentProposalPLine?.confirmedPortfolioId !== null;

      if (isCurrentPLineSelected && !isDependentPLineSelected) {
        invalidProductLines.push(pLine.id);
        notSelectedDependentPLines.push(currentDomainPLine.dependentOnProductLineId);
      }
    });

    return { invalidPLines: invalidProductLines, notSelectedDependentPLines };
  }

  public get dovetailingRuleCheck(): boolean {
    const std = this.proposal.productLines.find((line) => line.id.includes('STD'));
    const ltd = this.proposal.productLines.find((line) => line.id.includes('LTD'));

    if (!std || !ltd) return true;

    // Elimination Period - 90 days, 180 days
    // Maximum Benefit Period - 11, 12, 13 weeks - 24, 25, 26
    const stdDovetailingDurations = getAttributeValues(std, 'Maximum benefit period', this.portfoliosByProductLine);
    const ltdDovetailingDurations = getAttributeValues(ltd, 'Elimination period', this.portfoliosByProductLine);

    if (stdDovetailingDurations.length === 0 || ltdDovetailingDurations.length === 0) {
      return true;
    }

    // Check if LTD Elimination Period has "90 days" - STD Maximum Benefit Period should have 11, 12, or 13 weeks
    if (ltdDovetailingDurations.includes('90 days')) {
      const validStdValues = ['11 weeks', '12 weeks', '13 weeks'];
      if (stdDovetailingDurations.some((value) => validStdValues.includes(value))) {
        return true;
      }
    }

    // Check if LTD Elimination Period has "180 days" - STD Maximum Benefit Period should have 24, 25, or 26 weeks
    if (ltdDovetailingDurations.includes('180 days')) {
      const validStdValues = ['24 weeks', '25 weeks', '26 weeks'];
      if (stdDovetailingDurations.some((value) => validStdValues.includes(value))) {
        return true;
      }
    }

    return false;
  }

  public get validationErrors(): ProposalValidationError[] {
    if (!this.isAllProductLinesSelected()) {
      return [];
    }

    const errors: ProposalValidationError[] = [];
    const crossSellErrors = this.getRequiredCrossSellValidationErrors();

    if (this.productLineValidityLists.invalidPLines.length > 0) {
      errors.push({
        type: 'DEPENDENT_PRODUCT_LINES',
        productLineIds: this.productLineValidityLists.notSelectedDependentPLines,
      });
    }

    if (!this.dovetailingRuleCheck) {
      errors.push({ type: 'DOVETAILING' });
    }

    if (crossSellErrors.length > 0) {
      errors.push(...crossSellErrors);
    }

    return errors;
  }

  private getRequiredCrossSellValidationErrors(): ProposalValidationError[] {
    const confirmedCartItems = this.cartItems.filter((cartItem) => cartItem.confirmed);

    return getRequiredCrossSellViolations(confirmedCartItems).map((violation) => ({
      type: 'REQUIRED_CROSS_SELL',
      addedPortfolioName: violation.addedPortfolioName,
      requiredPortfolioName:
        this.cartItems.find((cartItem) => cartItem.portfolioId === violation.requiredPortfolioId)?.portfolioName ??
        PRODUCT_LINES_CONTENT_MAP[violation.requiredProductLineId].label,
    }));
  }

  // Check if all product lines were skipped, so don't let them confirm proposal without any selected line
  private isAllProductLinesDontProvide(): boolean {
    return this.proposal.productLines.every((pLine) => pLine.productType === ProductType.DONT_PROVIDE);
  }

  private enrollmentPeriodNotYetSubmitted(): boolean {
    return this.enrollmentPeriod.stage !== EnrollmentStage.SUBMITTED;
  }

  private canConfirmProposal(): boolean {
    return (
      this.isAllProductLinesSelected() &&
      !this.isAllProductLinesDontProvide() &&
      this.isProposalEditable &&
      this.validationErrors.length === 0
    );
  }

  // We should filter out all bc variant if the Proposal is not editable anymore
  private filterOutAllOtherBC(cartItem: CartItem): CartItem {
    if (!this.isProposalEditable) {
      const selectedBc = cartItem.browsingCriteria[cartItem.selectedIndex];
      // eslint-disable-next-line no-param-reassign
      cartItem.browsingCriteria = { 0: selectedBc };
    }
    return cartItem;
  }

  // eslint-disable-next-line class-methods-use-this
  private populatePortfolioByProductLine(
    portfolios: Portfolio[],
    attributes: ProductLineAttr[],
    cartItems: CartItem[],
  ): PortfolioItemsByProductLine[] {
    const portfolioItemsByProductLine: PortfolioItemsByProductLine[] = [];
    const productLineIdsInTheCart = [...new Set(cartItems.map((item) => item.productLineId))];
    productLineIdsInTheCart.forEach((productLineId) => {
      const portfolioIdsByLine = cartItems
        .filter((item) => item.productLineId === productLineId)
        .map((item) => item.portfolioId);
      const portfoliosByLine = portfolios
        .filter((portfolio) => portfolioIdsByLine.includes(portfolio.id))
        .map((portfolio) => {
          const cartItem = cartItems.find((item) => item.portfolioId === portfolio.id);
          if (cartItem) {
            return {
              portfolio,
              cartItem: this.filterOutAllOtherBC(cartItem),
            };
          }
          throw new Error(`Cart was corrupted and doesn't have cartItem with id ${portfolio.id}`);
        });
      portfolioItemsByProductLine.push({
        productLineId,
        portfolioToCartItems: portfoliosByLine,
        productLineAttributes: attributes.filter(
          (attr) => attr.productLine.replaceAll(' ', '') === PRODUCT_LINES_CONTENT_MAP[productLineId].joinedLabel,
        ),
      });
    });

    this.addRenewalProductLines(portfolioItemsByProductLine);
    /** Drops cart sections whose product line belongs to a different rate segment than the proposal. */
    return portfolioItemsByProductLine.filter(
      (item) => PRODUCT_LINES_CONTENT_MAP[item.productLineId]?.rateSegment === this.proposal.rateSegment,
    );
  }

  private addRenewalProductLines(portfolioItemsByProductLine: PortfolioItemsByProductLine[]): void {
    const linesWithCartItems: ProductLineId[] = portfolioItemsByProductLine.map((item) => item.productLineId);
    this.proposal.productLines
      .map((line) => line)
      .filter((line) => !linesWithCartItems.includes(line.id))
      .forEach((line) =>
        portfolioItemsByProductLine.push({
          productLineId: line.id,
          portfolioToCartItems: [],
          productLineAttributes: [],
        }),
      );
  }

  private loopOverPortfoliosByLine(action: (cartItem: CartItem) => void, productLineId?: string): void {
    this.portfoliosByProductLine
      .filter((item) => !productLineId || item.productLineId === productLineId)
      .map((item) => item.portfolioToCartItems)
      .forEach((list) => {
        list.forEach((pair) => {
          action(pair.cartItem);
        });
      });
  }

  private updateCartAfterUpdatingPortfolioBC(cart: Cart): void {
    this.cart.lastUpdateTime = cart.lastUpdateTime;
    this.cart.lastUpdateBrowsingCriteria = cart.lastUpdateBrowsingCriteria;
  }

  public get loadingGetter(): boolean {
    return this.loading;
  }

  public getInstalledProductLine(productLineId: ProductLineId): InstalledProductLine | undefined {
    return this.enrollmentPeriod.installedProposal?.productLines.find((line) => line.id === productLineId);
  }

  public isRenewalProductLine(productLineId: ProductLineId): boolean {
    return !!this.getInstalledProductLine(productLineId);
  }

  public get areRenewalActionsDisabled(): boolean {
    if (!this.isProposalEditable) {
      return true;
    }
    const installedProductLineIds = new Set(
      this.enrollmentPeriod.installedProposal?.productLines.map((productLine) => productLine.id) ?? [],
    );
    const hasAddedProductLines = this.proposal.productLines.some(
      (productLine) => !installedProductLineIds.has(productLine.id),
    );

    return isEffectiveDatePassed(this.enrollmentPeriod.effectiveDate) && !hasAddedProductLines;
  }

  public get isMutableProposal(): boolean {
    // Renewal stays editable when Change Plan or Add Benefit is on. Add Quote can still hide Change Plan per line.
    const canMutateRenewal =
      this.rootStore.domainStoreV2.isChangePlanActionAvailable(this.rootStore.userStore.userRoles) ||
      this.rootStore.domainStoreV2.isAddBenefitActionAvailable;

    return (
      !eeRights(this.rootStore.userStore.userRoles) &&
      !this.proposal.confirmed &&
      (!this.isRenewalEnrollment || canMutateRenewal)
    );
  }

  private get isRenewalEnrollment(): boolean {
    return this.enrollmentPeriod.type === EnrollmentType.RENEWAL;
  }

  /** Edit Proposal flow for carriers that support the new initiation flow */
  public async copyCartAndInitiateProposal(): Promise<void> {
    this.setLoading(true);
    const cartName = `Copy of ${this.cart.cartName}`;
    const newCart = await copyCart(this.cart.id, cartName);

    return runInAction(async () => {
      const initiateType =
        this.proposal.createdBy?.id === SYSTEM_GENERATED ? InitiateType.EDIT_RENEW_AS_IS : InitiateType.REGULAR;
      const [period] = await Promise.all([
        initiateProposal(
          {
            groupId: this.group.id,
            cartName,
            cartId: newCart.id,
            effectiveDate: this.cart.lastUpdateBrowsingCriteria.effectiveDate,
            productLines: [],
          },
          newCart.rateSegment,
          initiateType,
        ),
        updateGroup(buildInputGroupFromGroup(this.group)),
      ]);
      return runInAction(() => {
        this.setLoading(false);
        const periodId = period.data?.initiateProposal?.id || '';
        const cartId = newCart.id || '';

        if (newCart.rateSegment === 'CUSTOM') {
          window.location.href = getRFQProposalChecklistURL({ periodId, cartId });
        } else {
          redirectToRoute(RouteV2Id.QUOTE_CHECKLIST, {
            groupId: this.group.id,
            periodId,
            cartId,
          });
        }
      });
    });
  }

  setLoading(status: boolean): void {
    this.loading = status;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.cart = INITIAL_CART_OBJECT;
    this.catalog = INITIAL_CATALOG;
    this.group = INITIAL_GROUP_OBJECT;
    this.enrollmentPeriod = INITIAL_ENROLLMENT_OBJECT;
    this.otherEnrollmentIsActiveForThisGroup = undefined;
    this.domainConfig = INITIAL_DOMAIN_CONFIGURATION;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
